import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type AttributeWriteDto,
  attributesApi,
  type BrandWriteDto,
  brandsApi,
  type CategoryAttributeAssignment,
  type CategoryWriteDto,
  categoriesApi,
  hubsApi,
  mediaApi,
  type OfferWriteDto,
  offersApi,
  type ProductWriteDto,
  packsApi,
  productsApi,
  type SetStockPayload,
  stockApi,
  type VariantWriteDto,
  variantsApi,
} from '@/lib/catalog/catalog-api';
import type { AddPackItemInput, Hub, ProductsListParams } from '@/lib/catalog/types';

// ─── Hubs ──────────────────────────────────────────────────────────────────
export const useHubs = () =>
  useQuery({
    queryKey: ['catalog', 'hubs'],
    queryFn: hubsApi.list,
    staleTime: 60_000,
  });

export const useCreateHub = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Hub>) => hubsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'hubs'] }),
  });
};

export const useUpdateHub = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Hub>) => hubsApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'hubs'] }),
  });
};

export const useDeleteHub = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hubsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'hubs'] }),
  });
};

// ─── Categories ────────────────────────────────────────────────────────────
export const useCategoriesTree = (archived = false) =>
  useQuery({
    queryKey: ['catalog', 'categories', 'tree', archived],
    queryFn: () => categoriesApi.tree(archived),
    staleTime: 30_000,
  });

export const useCategoriesList = (params: { search?: string; isActive?: boolean } = {}) =>
  useQuery({
    queryKey: ['catalog', 'categories', 'list', params],
    queryFn: () => categoriesApi.list(params),
    staleTime: 30_000,
  });

export const useCategory = (id: string | undefined) =>
  useQuery({
    queryKey: ['catalog', 'categories', id],
    queryFn: () => categoriesApi.get(id!),
    enabled: !!id,
  });

export const useCreateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CategoryWriteDto) => categoriesApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useUpdateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryWriteDto }) =>
      categoriesApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useArchiveCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useUnarchiveCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.unarchive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useReassignDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetCategoryId }: { id: string; targetCategoryId: string }) =>
      categoriesApi.reassignDelete(id, targetCategoryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

export const useDeleteCategoryWithProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.deleteWithProducts(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }),
  });
};

// ─── Brands (référentiel T09-9.2) ────────────────────────────────────────────
export const useBrands = (params: { search?: string; isActive?: boolean } = {}) =>
  useQuery({
    queryKey: ['catalog', 'brands', 'list', params],
    queryFn: () => brandsApi.list(params),
    staleTime: 30_000,
  });

export const useCreateBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BrandWriteDto) => brandsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }),
  });
};

export const useUpdateBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: BrandWriteDto }) =>
      brandsApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }),
  });
};

export const useDeleteBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => brandsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }),
  });
};

export const useMergeBrands = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceIds, targetId }: { sourceIds: string[]; targetId: string }) =>
      brandsApi.merge(sourceIds, targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'brands'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
    },
  });
};

// ─── Attributes (caractéristiques structurées T09-9.1) ───────────────────────
export const useAttributes = () =>
  useQuery({
    queryKey: ['catalog', 'attributes', 'list'],
    queryFn: attributesApi.list,
    staleTime: 30_000,
  });

export const useCategoryAttributes = (categoryId: string | undefined) =>
  useQuery({
    queryKey: ['catalog', 'attributes', 'category', categoryId],
    queryFn: () => attributesApi.forCategory(categoryId!),
    enabled: !!categoryId,
    staleTime: 30_000,
  });

export const useCreateAttribute = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AttributeWriteDto) => attributesApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'attributes'] }),
  });
};

export const useUpdateAttribute = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AttributeWriteDto }) =>
      attributesApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'attributes'] }),
  });
};

export const useDeleteAttribute = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => attributesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'attributes'] }),
  });
};

export const useSetCategoryAttributes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      assignments,
    }: {
      categoryId: string;
      assignments: CategoryAttributeAssignment[];
    }) => attributesApi.setForCategory(categoryId, assignments),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'attributes'] }),
  });
};

// ─── Products ──────────────────────────────────────────────────────────────
export const useProductStats = () =>
  useQuery({
    queryKey: ['catalog', 'products', 'stats'],
    queryFn: productsApi.stats,
    staleTime: 10_000,
  });

export const useProductsList = (params: ProductsListParams) =>
  useQuery({
    queryKey: ['catalog', 'products', 'list', params],
    queryFn: () => productsApi.list(params),
    staleTime: 5_000,
  });

export const useProduct = (id: string | undefined) =>
  useQuery({
    queryKey: ['catalog', 'products', id],
    queryFn: () => productsApi.get(id!),
    enabled: !!id,
  });

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductWriteDto) => productsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products'] }),
  });
};

export const useUpdateProduct = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductWriteDto) => productsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', id] });
    },
  });
};

export const useDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products'] }),
  });
};

export const useProductTransition = (action: 'publish' | 'unpublish' | 'archive') => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      action === 'publish'
        ? productsApi.publish(id)
        : action === 'unpublish'
          ? productsApi.unpublish(id)
          : productsApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products'] }),
  });
};

// ─── Packs (P18) ───────────────────────────────────────────────────────────
export const usePackPreview = (productId: string | undefined) =>
  useQuery({
    queryKey: ['catalog', 'packs', productId],
    queryFn: () => packsApi.preview(productId!),
    enabled: !!productId,
  });

const usePackInvalidate = (productId: string) => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['catalog', 'packs', productId] });
    void qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] });
  };
};

export const useAddPackItem = (productId: string) => {
  const invalidate = usePackInvalidate(productId);
  return useMutation({
    mutationFn: (payload: AddPackItemInput) => packsApi.addItem(productId, payload),
    onSuccess: invalidate,
  });
};

export const useUpdatePackItem = (productId: string) => {
  const invalidate = usePackInvalidate(productId);
  return useMutation({
    mutationFn: ({
      itemId,
      ...payload
    }: { itemId: string } & Partial<Omit<AddPackItemInput, 'componentVariantId'>>) =>
      packsApi.updateItem(itemId, payload),
    onSuccess: invalidate,
  });
};

export const useRemovePackItem = (productId: string) => {
  const invalidate = usePackInvalidate(productId);
  return useMutation({
    mutationFn: (itemId: string) => packsApi.removeItem(itemId),
    onSuccess: invalidate,
  });
};

export const useReplacePackItems = (productId: string) => {
  const invalidate = usePackInvalidate(productId);
  return useMutation({
    mutationFn: (items: AddPackItemInput[]) => packsApi.replaceItems(productId, items),
    onSuccess: invalidate,
  });
};

export const useEnhanceProductImages = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => productsApi.enhanceImages(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', id] });
    },
  });
};

/** Reprend UNE image, sans consommer le traitement global ni toucher aux autres. */
export const useEnhanceOneImage = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => productsApi.enhanceOneImage(id, mediaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', id] });
    },
  });
};

export const useDeleteProductMedia = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => productsApi.removeMedia(id, mediaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', id] });
    },
  });
};

// ─── Variants ──────────────────────────────────────────────────────────────
export const useCreateVariant = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VariantWriteDto & { name: string }) =>
      variantsApi.create(productId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

export const useUpdateVariant = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: VariantWriteDto }) =>
      variantsApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

export const useDeleteVariant = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      variantsApi.remove(id, force),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

/** Passage en produit simple : aperçu (lecture) puis conversion. */
export const useSimpleConversionPreview = (productId: string, keepVariantId?: string, enabled = true) =>
  useQuery({
    queryKey: ['catalog', 'products', productId, 'convert-to-simple', keepVariantId ?? 'defaut'],
    queryFn: () => productsApi.previewConvertToSimple(productId, keepVariantId),
    enabled,
    staleTime: 0,
  });

export const useConvertToSimple = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keepVariantId: string) => productsApi.convertToSimple(productId, keepVariantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

// ─── Offers ────────────────────────────────────────────────────────────────
export const useCreateOffer = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      payload,
    }: {
      variantId: string;
      payload: OfferWriteDto & { price: number };
    }) => offersApi.create(variantId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

export const useUpdateOffer = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: OfferWriteDto }) =>
      offersApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

export const useDeleteOffer = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => offersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

// ─── Stock ─────────────────────────────────────────────────────────────────
export const useSetStock = (productId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetStockPayload) => stockApi.set(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products', productId] }),
  });
};

// ─── Media / médiathèque (T09-9.4) ───────────────────────────────────────────
export const useMediaLibrary = (params: Parameters<typeof mediaApi.list>[0] = {}) =>
  useQuery({
    queryKey: ['catalog', 'media', 'list', params],
    queryFn: () => mediaApi.list(params),
    staleTime: 10_000,
  });

export const useMissingVisual = (params: Parameters<typeof mediaApi.missingVisual>[0] = {}) =>
  useQuery({
    queryKey: ['catalog', 'media', 'missing', params],
    queryFn: () => mediaApi.missingVisual(params),
    staleTime: 10_000,
  });

export const useDeleteMedia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mediaApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'media'] }),
  });
};

export const useUploadMedia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, altText }: { file: File; altText?: string }) =>
      mediaApi.upload(file, { altText }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'media'] }),
  });
};

export const useUpdateMedia = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, altText }: { id: string; altText: string }) =>
      mediaApi.update(id, { altText }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'media'] }),
  });
};
